import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Upload, AlertTriangle } from "lucide-react";
import { client, errMsg } from "@/lib/api";
import { today } from "@/lib/format";

const LABELS = { families: "Families", members: "People", payments: "Money entries", fees: "Fees", schedule: "Schedule items", questions: "Form questions", activities: "Activities", teams: "Teams", photo_metadata: "Old photo records (not imported)" };

export default function BackupTab() {
  const [raw, setRaw] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const api = client("admin");

  const download = async () => {
    const { data } = await api.get("/admin/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `fanene-reunion-backup-${today()}.json` });
    a.click();
    URL.revokeObjectURL(url);
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const json = JSON.parse(await file.text());
      setRaw(json);
      setPreview((await api.post("/admin/import/preview", json)).data);
    } catch (err) { toast.error(err instanceof SyntaxError ? "That file isn't valid JSON." : errMsg(err)); }
  };

  const commit = async () => {
    if (!window.confirm("This replaces the website's families, payments, schedule, fees, questions, activities and teams with the backup. Continue?")) return;
    setBusy(true);
    try { await api.post("/admin/import/commit", raw); toast.success("Backup imported. The site is live with your data."); setPreview(null); setRaw(null); qc.invalidateQueries(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-warm space-y-4">
        <Download className="w-8 h-8 text-[#2B5338]" />
        <h2 className="text-3xl font-bold text-amber-950">Download all data</h2>
        <p className="text-stone-700">Saves everything (details, fees, schedule, questions, activities, teams, families, payments, photo list) as one JSON file.</p>
        <button data-testid="export-json-backup-button" onClick={download} className="btn-secondary"><Download className="w-5 h-5" />Download backup</button>
      </div>
      <div className="card-warm space-y-4">
        <Upload className="w-8 h-8 text-[#8C3415]" />
        <h2 className="text-3xl font-bold text-amber-950">Import phone-app backup</h2>
        <p className="text-stone-700">Choose your <b>fanene-reunion-backup.json</b>. You'll see what was found before anything changes.</p>
        <input type="file" accept=".json,application/json" data-testid="import-json-file-input" onChange={pick}
          className="block w-full file:mr-4 file:min-h-[48px] file:px-5 file:rounded-full file:border-0 file:bg-[#8C3415] file:text-white file:font-semibold" />
        {preview && (
          <div className="space-y-4" data-testid="import-preview">
            <p className="text-lg">Found: <b>{preview.summary.title}</b></p>
            <ul className="grid grid-cols-2 gap-2">
              {Object.entries(LABELS).map(([k, l]) => <li key={k} data-testid={`import-count-${k}`} className="rounded-xl bg-[#FDF6ED] px-3 py-2"><b className="font-heading text-xl">{preview.summary[k]}</b> <span className="text-stone-700">{l}</span></li>)}
            </ul>
            {preview.sample_families.length > 0 && <p className="text-stone-700">First families: {preview.sample_families.map((f) => f.family_name).join(", ")}</p>}
            <p className="flex gap-2 text-amber-900"><AlertTriangle className="w-5 h-5 shrink-0" />Importing replaces current website data. Organizer accounts and photos are kept.</p>
            <button disabled={busy} data-testid="import-json-backup-button" onClick={commit} className="btn-primary">{busy ? "Importing…" : "Import this backup"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
